// Callback ownership labels (2026-09-23, vault #233 Option B, #234).
//
// The Scheduled Callbacks list never said whether a row was personal (routed
// to one agent's own quick connect) or general (anyone available). The reader
// now returns, on a personal row, assignedTo:{username,name} (username = the
// agent's Connect login, name = display name, never an email). The console
// compares username with its own Connect login and labels the row. Pure, so
// scripts/verify-callback-owner-label.mjs can drive every case.

// "Assigned to you" / "Assigned to <Name>" / "Anyone available".
// A personal row the reader could not attribute (no routing row, old bundle
// of the reader, or an agent with no usable name) is still personal — it says
// so without inventing a name.
export function ownerLabel(row, agentUsername) {
  if (!row || row.routedTo !== 'agent') return { kind: 'anyone', text: 'Anyone available' };
  const me = String(agentUsername || '').trim();
  const who = row.assignedTo || null;
  const username = String(who?.username || '').trim();
  if (me && username && username === me) return { kind: 'you', text: 'Assigned to you' };
  const name = String(who?.name || '').trim();
  if (name && !name.includes('@')) return { kind: 'other', text: `Assigned to ${name}` };
  return { kind: 'other', text: 'Assigned to an agent' };
}

// The handover card's Queue field. Connect routes a personal callback task to
// the agent's OWN queue — an AGENT-type queue whose ARN is
// …/instance/<i>/queue/agent/<agentUserId> and whose name is empty in Streams
// (vault #234; Streams' Queue.queueId is an alias of queueARN, never a bare
// id — review 2026-09-23). So the card used to show "—". A named queue is
// shown as is; an unnamed TASK queue reads "Your queue" ONLY when its ARN's
// agent id is this agent's user id; anything else stays "—" (never a guess).
// 2026-10-06 (backlog 95, Storm): every client has its own queue, named
// `knktr-<client id>` in Amazon Connect so reports and scripts have a stable
// name. An agent should see the CLIENT, not that id: a client queue is shown as
// the contact's brand name ("knktr.ai", "Aurora Creative Agency"). Any other
// named queue is shown as it is; no brand on the contact = the queue's own name.
export function queueLabel({ name = null, queueArn = null, channel = null, agentId = null, brandName = null } = {}) {
  const n = String(name || '').trim();
  const brand = String(brandName || '').trim();
  if (n) return brand && /^knktr-[a-z][a-z0-9-]{2,19}$/.test(n) ? brand : n;
  if (String(channel || '').toUpperCase() !== 'TASK') return '—';
  const owner = agentQueueOwnerFromArn(queueArn);
  return owner && agentId && owner === String(agentId) ? 'Your queue' : '—';
}

// The Queue cell straight from the Streams objects (defensive: every read can
// throw on a half-built contact, D21). This is what ContactContext calls, so
// the test drives the same reads the console makes.
export function queueMetaFrom(contact, agent = null, brandName = null) {
  let name = null; let queueArn = null; let channel = null; let agentId = null;
  try { const q = contact?.getQueue?.(); name = q?.name ?? null; queueArn = q?.queueARN ?? q?.queueId ?? null; } catch { /* no queue */ }
  try { channel = contact?.getType?.() ?? null; } catch { /* unknown */ }
  try { agentId = agentIdFromArn(agent?.getConfiguration?.()?.agentARN); } catch { /* unknown */ }
  return queueLabel({ name, queueArn, channel, agentId, brandName });
}

// The agent's user id from the Streams configuration ARN (…/agent/<id>).
export function agentIdFromArn(arn) {
  const m = String(arn || '').match(/\/agent\/([^/]+)$/);
  return m ? m[1] : null;
}

// The owning agent's user id from an AGENT queue ARN (…/queue/agent/<id>);
// null for a standard queue (…/queue/<id>) or anything else.
export function agentQueueOwnerFromArn(arn) {
  const m = String(arn || '').match(/\/queue\/agent\/([^/]+)$/);
  return m ? m[1] : null;
}
